/** Cached native PowerPoint charts rendered as an SVG preview, without fetching
 * workbook links or changing the editable chart in the downloadable PPTX. */
const body = (xml, tag) => xml.match(new RegExp(`<c:${tag}\\b[^>]*>([\\s\\S]*?)<\\/c:${tag}>`))?.[1] || '';
const escape = text => String(text).replace(/[<>&"']/g, char => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[char]);
const decode = text => String(text).replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'");
const points = xml => [...xml.matchAll(/<c:pt\b[^>]*idx="(\d+)"[^>]*>([\s\S]*?)<\/c:pt>/g)]
  .slice(0, 100).map(match => [Number(match[1]), decode(body(match[2], 'v'))]).sort((a, b) => a[0] - b[0]);
const color = (xml, fallback) => `#${xml.match(/<a:srgbClr\b[^>]*val="([0-9a-f]{6})"/i)?.[1] || fallback}`;

export function chartPreviewFromXml(xml, ink = '1a1814') {
  // Do not misrepresent combo/stacked/scatter charts as ordinary columns.
  const kinds = [...xml.matchAll(/<c:([a-zA-Z]+Chart)\b/g)].map(m => m[1]);
  if (kinds.length !== 1 || !['barChart', 'lineChart', 'pieChart'].includes(kinds[0])
      || /<c:grouping\b[^>]*val="(?:stacked|percentStacked)"/.test(xml)) return null;
  const kind = kinds[0];
  const series = [...xml.matchAll(/<c:ser\b[^>]*>([\s\S]*?)<\/c:ser>/g)].slice(0, 6).map(match => {
    const s = match[1];
    const categories = new Map(points(body(s, 'cat')));
    const values = points(body(s, 'val'));
    const cachedName = points(body(s, 'tx'))[0]?.[1];
    return {
      name: cachedName || decode(body(body(s, 'tx'), 'v')),
      color: color(body(s, 'spPr'), '2A5640'),
      data: values.filter(([i, v]) => categories.has(i) && v.trim() && Number.isFinite(Number(v)))
        .map(([i, v]) => ({ label: categories.get(i), value: Number(v) })),
    };
  }).filter(s => s.data.length);
  if (!series.length || (kind === 'pieChart' && (series.length !== 1 || series[0].data.length > 12))) return null;
  const labels = series[0].data.map(p => p.label);
  if (series.some(s => s.data.length !== labels.length || s.data.some((p, i) => p.label !== labels[i]))) return null;
  const W = 960; const H = 460;
  const L = 95; const R = 30; const T = 25; const B = 75;
  const pw = W - L - R; const ph = H - T - B;
  const fg = `#${ink}`;
  const text = (x, y, value, anchor = 'middle', size = 20) => `<text x="${x}" y="${y}" text-anchor="${anchor}" font-family="Arial, sans-serif" font-size="${size}" fill="${fg}">${escape(value)}</text>`;
  let drawing = '';
  if (kind === 'pieChart') {
    if (series[0].data.some(p => p.value < 0)) return null;
    const total = series[0].data.reduce((sum, p) => sum + p.value, 0);
    if (!total) return null;
    const palette = [series[0].color, '#5C6C62', '#A3B49E', '#C4A574', '#728EA0', '#A97163'];
    let angle = -Math.PI / 2;
    series[0].data.forEach((p, i) => {
      const end = angle + p.value / total * Math.PI * 2;
      const cx = 310; const cy = 220; const r = 180;
      const x = a => cx + Math.cos(a) * r; const y = a => cy + Math.sin(a) * r;
      if (p.value === total) drawing += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${palette[i % palette.length]}"/>`;
      else drawing += `<path d="M ${cx} ${cy} L ${x(angle)} ${y(angle)} A ${r} ${r} 0 ${end - angle > Math.PI ? 1 : 0} 1 ${x(end)} ${y(end)} Z" fill="${palette[i % palette.length]}"/>`;
      drawing += text(545, 55 + i * 32, `${p.label}: ${p.value} (${Math.round(p.value / total * 100)}%)`, 'start', 18);
      angle = end;
    });
  } else {
    const values = series.flatMap(s => s.data.map(p => p.value));
    const min = Math.min(0, ...values); const max = Math.max(0, ...values) || 1;
    const span = max - min || 1;
    const horizontal = kind === 'barChart' && /<c:barDir\b[^>]*val="bar"/.test(xml);
    const y = value => T + ph * (max - value) / span;
    const x = value => L + pw * (value - min) / span;
    for (let i = 0; i <= 4; i++) {
      const value = min + span * i / 4;
      const formatted = Number(value.toPrecision(3)).toLocaleString('en-US');
      drawing += horizontal
        ? `<path d="M ${x(value)} ${T} V ${T + ph}" stroke="${fg}" opacity=".12"/>${text(x(value), T + ph + 30, formatted)}`
        : `<path d="M ${L} ${y(value)} H ${W - R}" stroke="${fg}" opacity=".12"/>${text(L - 12, y(value) + 7, formatted, 'end')}`;
    }
    const step = (horizontal ? ph : pw) / labels.length;
    labels.forEach((label, i) => {
      drawing += horizontal ? text(L - 12, T + step * (i + .5) + 7, label, 'end', 18)
        : text(L + step * (i + .5), T + ph + 30, label, 'middle', 18);
    });
    series.forEach((s, si) => {
      if (kind === 'lineChart') {
        const path = s.data.map((p, i) => `${i ? 'L' : 'M'} ${L + step * (i + .5)} ${y(p.value)}`).join(' ');
        drawing += `<path d="${path}" fill="none" stroke="${s.color}" stroke-width="4"/>`;
        s.data.forEach((p, i) => { drawing += `<circle cx="${L + step * (i + .5)}" cy="${y(p.value)}" r="5" fill="${s.color}"/>`; });
      } else {
        const bw = step * .68 / series.length;
        s.data.forEach((p, i) => {
          const offset = step * (i + .16) + si * bw;
          drawing += horizontal
            ? `<rect x="${Math.min(x(0), x(p.value))}" y="${T + offset}" width="${Math.abs(x(p.value) - x(0))}" height="${bw}" fill="${s.color}"/>`
            : `<rect x="${L + offset}" y="${Math.min(y(0), y(p.value))}" width="${bw}" height="${Math.abs(y(0) - y(p.value))}" fill="${s.color}"/>`;
        });
      }
      drawing += `<rect x="${L + si * 250}" y="${H - 18}" width="12" height="12" fill="${s.color}"/>${text(L + 18 + si * 250, H - 6, s.name, 'start', 16)}`;
    });
  }
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${drawing}</svg>`;
  return { src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`, alt: series.map(s => `${s.name}: ${s.data.map(p => `${p.label} ${p.value}`).join(', ')}`).join('; ') };
}
